import React, { useEffect, useState } from "react";
import Paper from "@mui/material/Paper";
import {
  Button,
  FormControlLabel,
  FormLabel,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { PreLoader } from "../../subcomponents/Preloader";
import { textStyle } from "../../styles/Style";
import "../../../components/styles/popupStyle.css";
import InfoIcon from "@mui/icons-material/Info";
import { checkDuplicate, deleteItem } from "../../actions/actionItems";
import AlertComponent from "../../subcomponents/AlertComponent";
import DraggableItemList from "../../subcomponents/DNDComponent";
import { taxExemptCategoryTooltip } from "../../../constants/TooltipInformation";
import {
  validateString,
  validationLength,
  validationType,
} from "../../actions/validations";
import {
  versaFieldErrors,
  versaFieldHelperText,
} from "../../../constants/VersaHelperTexts";
import { tooltipComponent } from "../../subcomponents/tooltipComponent";
import { buttonValues, DNDFieldsEnum } from "../../../constants/Types";
import { ClearButton } from "../../subcomponents/ClearButton";
import { RoomPropertyEnum } from "../../../constants/NavbarList";
import { TutorialComponent } from "../../Tour/DemoTour";
import { CopyButton } from "../../subcomponents/CopyButton";
import useScrollToTop from "../../hooks/useScrollToTop";

const TaxExemptCategory = ({
  data,
  fieldValues,
}: {
  data: any;
  fieldValues: any;
}) => {
  const [taxExemptCode, setTaxExemptCode] = useState<string | null>(null);
  const [codeError, setCodeError] = useState<boolean>(false);
  const [taxExemptCategoryName, setTaxExemptCategoryName] = useState<
    string | null
  >(null);
  const [nameError, setNameError] = useState<boolean>(false);
  const [taxExemptInformation, setTaxExemptInformation] = useState<any>(null);
  const [active, setActive] = useState<boolean>(true);
  const [alertMessage, setAlertMessage] = useState<string>("");
  const [openAlert, setOpenAlert] = useState<boolean>(false);
  const [alertType, setAlertType] = useState<"success" | "error">("success");
  const [editClicked, setEditClicked] = useState<boolean>(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const isTourCompleted =
    localStorage.getItem("isTourCompleted") === "true" ||
    !localStorage.getItem("isTourCompleted");
  const [lastUpdatedRecord, setLastUpdatedRecord] = useState<any>(null);
  const { topRef, scrollToTop } = useScrollToTop();

  const taxExemptPayload = {
    code: taxExemptCode ? String(taxExemptCode).trim() : taxExemptCode,
    name: taxExemptCategoryName
      ? String(taxExemptCategoryName).trim()
      : taxExemptCategoryName,
    isActive: active,
  };

  const addTaxExemptInformation = () => {
    if (!validateInput()) {
      return;
    }
    if (
      checkDuplicate(
        Array.isArray(taxExemptInformation) ? taxExemptInformation : [],
        DNDFieldsEnum.TAXEXEMPTCODE,
        taxExemptPayload.code,
      )
    ) {
      setAlertMessage("Cannot have duplicate entries");
      setAlertType("error");
      setOpenAlert(true);
      return;
    }

    setTaxExemptInformation((prev: any) =>
      Array.isArray(prev)
        ? [
            ...prev,
            {
              id: prev.length + 1,
              ...taxExemptPayload,
            },
          ]
        : [
            {
              id: 1,
              ...taxExemptPayload,
            },
          ],
    );
    setLastUpdatedRecord(taxExemptPayload);
    editTaxExemptInformation(null);
    setEditClicked(false);
  };

  const editTaxExemptInformation = (list: any) => {
    list?.id && setSelectedId(list.id);
    list !== null && setEditClicked(true);
    setTaxExemptCode(list?.code);
    setTaxExemptCategoryName(list?.name);
    setActive(list ? list.isActive : true);
    setCodeError(false);
    setNameError(false);
  };

  const updateTaxExemptInformation = () => {
    if (!validateInput()) {
      return;
    }
    if (
      checkDuplicate(
        (Array.isArray(taxExemptInformation)
          ? taxExemptInformation
          : []
        ).filter((item: any) => String(item.id) !== String(selectedId)),
        DNDFieldsEnum.TAXEXEMPTCODE,
        taxExemptPayload.code,
      )
    ) {
      setAlertMessage("Cannot have duplicate entries");
      setAlertType("error");
      setOpenAlert(true);
      return;
    }
    setTaxExemptInformation((prev: any) =>
      Array.isArray(prev)
        ? prev.map((item: any) => {
            if (item.id === selectedId) {
              return {
                ...item,
                ...taxExemptPayload,
              };
            }
            return item;
          })
        : prev,
    );
    editTaxExemptInformation(null);
    setEditClicked(false);
    setSelectedId(null);
    setAlertMessage(
      `${RoomPropertyEnum.taxExemptCategory} updated successfully`,
    );
    setAlertType("success");
    setOpenAlert(true);
  };

  const deleteTaxExemptInformation = (id: number) => {
    setTaxExemptInformation(deleteItem(taxExemptInformation, id));
  };

  const validateInput = (): boolean => {
    setCodeError(false);
    setNameError(false);
    let isValid = true;

    const trimmedCode = String(taxExemptCode ?? "").trim();
    if (!trimmedCode) {
      setCodeError(true);
      isValid = false;
    } else if (
      !validateString(
        trimmedCode,
        validationLength.taxExemptCode,
        validationType.CODE,
      )
    ) {
      setCodeError(true);
      isValid = false;
    }

    const trimmedName = String(taxExemptCategoryName ?? "").trim();
    if (!trimmedName) {
      setNameError(true);
      isValid = false;
    } else if (
      !validateString(
        trimmedName,
        validationLength.roomFeatureName,
        validationType.NAME,
      )
    ) {
      setNameError(true);
      isValid = false;
    }

    return isValid;
  };

  useEffect(() => {
    data(taxExemptInformation);
  }, [taxExemptInformation]);

  useEffect(() => {
    fieldValues &&
      setTaxExemptInformation(
        Array.isArray(fieldValues)
          ? fieldValues
          : fieldValues
            ? [fieldValues]
            : null,
      );
  }, []);

  const taxExemptCodeHelperText = codeError
    ? !(taxExemptCode?.trim() ?? "")
      ? versaFieldErrors.required
      : versaFieldErrors.taxExemptCategory.taxExemptCode
    : versaFieldHelperText.taxExemptCategory.taxExemptCode;

  const taxExemptCategoryNameHelperText = nameError
    ? !(taxExemptCategoryName?.trim() ?? "")
      ? versaFieldErrors.required
      : versaFieldErrors.taxExemptCategory.taxExemptCategoryName
    : versaFieldHelperText.taxExemptCategory.taxExemptCategoryName;

  return (
    <Stack direction="column" gap="10px">
      <div ref={topRef} />
      <Stack direction="column" width="100%">
        <Paper
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "10px",
            padding: "20px",
          }}
        >
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            width="100%"
            gap="10px"
            mb={4}
          >
            <Typography variant="h6" component="div" sx={textStyle}>
              Tax Exempt Information
              <Tooltip
                title={tooltipComponent(taxExemptCategoryTooltip.header)}
              >
                <InfoIcon fontSize="small" />
              </Tooltip>
            </Typography>
            <Stack direction="row" gap="10px" justifyContent="space-between">
              {editClicked && (
                <ClearButton
                  click={() => {
                    editTaxExemptInformation(null);
                    setSelectedId(null);
                  }}
                />
              )}
              {lastUpdatedRecord && (
                <CopyButton
                  click={() => {
                    editTaxExemptInformation(lastUpdatedRecord);
                  }}
                />
              )}
            </Stack>
          </Stack>
          <Stack
            className="form-two-col-row"
            direction="row"
            gap="20px"
            width="100%"
          >
            <Stack direction="column" width="100%" gap="5px">
              <FormLabel component="legend">
                Tax Exempt Code
                <Tooltip
                  title={tooltipComponent(
                    taxExemptCategoryTooltip.taxExemptCategoryCode,
                  )}
                >
                  <InfoIcon fontSize="small" />
                </Tooltip>
              </FormLabel>
              <TextField
                id="taxExemptCode"
                variant="outlined"
                placeholder="Tax Exempt Code"
                className="taxExemptCode"
                error={codeError}
                helperText={taxExemptCodeHelperText}
                value={taxExemptCode ?? ""}
                sx={{ width: "100%" }}
                onChange={(e: any) => {
                  setTaxExemptCode(e.target.value.toUpperCase() || null);
                  setCodeError(false);
                }}
              />
            </Stack>
            <Stack direction="column" width="100%" gap="5px">
              <FormLabel component="legend">
                Tax Exempt Category Name
                <Tooltip
                  title={tooltipComponent(
                    taxExemptCategoryTooltip.taxExemptCategoryName,
                  )}
                >
                  <InfoIcon fontSize="small" />
                </Tooltip>
              </FormLabel>
              <TextField
                id="taxExemptCategoryName"
                variant="outlined"
                placeholder="Tax Exempt Category Name"
                error={nameError}
                helperText={taxExemptCategoryNameHelperText}
                value={taxExemptCategoryName ?? ""}
                sx={{ width: "100%" }}
                onChange={(e: any) => {
                  setTaxExemptCategoryName(e.target.value || null);
                  setNameError(false);
                }}
              />
            </Stack>
          </Stack>
          <Stack direction="row" width="100%" gap="2px">
            <FormControlLabel
              control={
                <Switch checked={active} onChange={() => setActive(!active)} />
              }
              label="Active?"
            />
            <Tooltip title={tooltipComponent(taxExemptCategoryTooltip.active)}>
              <InfoIcon fontSize="small" />
            </Tooltip>
          </Stack>
          <Stack direction="row" width="100%" justifyContent="flex-end">
            <Button
              variant="contained"
              sx={{
                cursor: "pointer",
                border: "1px solid gray",
                borderRadius: "10px",
                background: "darkgreen",
                color: "white",
              }}
              onClick={
                selectedId
                  ? updateTaxExemptInformation
                  : addTaxExemptInformation
              }
              id="addNewTaxExemptInformation"
            >
              {selectedId ? buttonValues.UPDATE : buttonValues.ADD} Tax Exempt
              Information
            </Button>
          </Stack>
          <Stack direction="column" gap="10px" mt={4}>
            <Typography variant="h6">
              Tax Exempt Information Available
            </Typography>
            <Typography variant="body2" sx={{ color: "red", fontWeight: 800 }}>
              Do not use sample data. Those are for demo purposes only!
            </Typography>
            <Stack direction="column" gap="10px">
              {taxExemptInformation ? (
                <DraggableItemList
                  itemData={taxExemptInformation}
                  onOrderUpdate={(order) => setTaxExemptInformation(order)}
                  onEdit={(info) => {
                    editTaxExemptInformation(info);
                    scrollToTop();
                  }}
                  onDelete={(id) => deleteTaxExemptInformation(id)}
                  code={DNDFieldsEnum.TAXEXEMPTCODE}
                  name={DNDFieldsEnum.TAXEXEMPTNAME}
                />
              ) : (
                <Typography variant="body2">
                  No Tax Exempt Information Available
                </Typography>
              )}
            </Stack>
          </Stack>
        </Paper>
      </Stack>
      <AlertComponent
        message={alertMessage}
        type={alertType}
        alertOpen={openAlert}
        closeAlert={() => setOpenAlert(false)}
      />
      {!isTourCompleted && (
        <TutorialComponent page={RoomPropertyEnum.taxExemptCategory} />
      )}
    </Stack>
  );
};

export default TaxExemptCategory;
